import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LangContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Loader2,
  LogOut,
  ChevronRight,
  ShieldCheck,
  FileText,
  HelpCircle,
} from "lucide-react";
import { toast } from "sonner";

export default function ProfileView() {
  const { user, login, register, logout, loading } = useAuth();
  const { lang, setLang, languages, t } = useLang();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success(t("app.tab_login"));
      } else {
        await register(email, password);
        toast.success(t("app.tab_register"));
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || err?.message || "Sign-in failed",
      );
    }
  };

  if (user) {
    const initials = (user.username || user.email || "U")
      .slice(0, 2)
      .toUpperCase();
    return (
      <div className="px-4 pt-6 pb-6 space-y-4" data-testid="profile-logged-in">
        <h1 className="font-display text-[28px] font-bold tracking-tight">
          {t("app.tab_profile")}
        </h1>

        <div className="bg-[#1c1c1e] rounded-2xl p-5 flex items-center gap-4 border border-white/[0.08]">
          <div className="h-16 w-16 rounded-full bg-[#007AFF]/15 border-2 border-[#007AFF]/30 flex items-center justify-center text-2xl font-bold text-[#007AFF]">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display text-xl font-bold truncate">
              {user.username || user.email}
            </p>
            <p className="text-sm text-white/50 truncate">{user.email}</p>
            <div className="flex gap-2 mt-2">
              <Badge className="bg-[#007AFF]/15 text-[#007AFF] border-[#007AFF]/30 text-[10px]">
                📡 ACTIVATOR
              </Badge>
            </div>
          </div>
        </div>

        <div className="bg-[#1c1c1e] rounded-2xl divide-y divide-white/5 border border-white/[0.08]">
          {[
            { icon: ShieldCheck, label: t("app.privacy") },
            { icon: FileText, label: t("app.terms") },
            { icon: HelpCircle, label: t("app.support") },
          ].map((it, i) => (
            <button
              key={i}
              data-testid={`profile-link-${i}`}
              className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-white/[0.02] transition-colors"
            >
              <it.icon className="h-[18px] w-[18px] text-[#06AED5]" />
              <span className="flex-1 text-start text-[15px]">{it.label}</span>
              <ChevronRight className="h-[18px] w-[18px] text-white/30" />
            </button>
          ))}
        </div>

        <Button
          onClick={async () => {
            await logout();
          }}
          data-testid="profile-logout-btn"
          className="w-full bg-[#FF453A]/10 hover:bg-[#FF453A]/20 border border-[#FF453A]/30 text-[#FF453A] h-12 rounded-2xl font-semibold"
        >
          <LogOut className="h-4 w-4 mr-2" />
          {mode === "login" ? "Logout" : "Logout"}
        </Button>
      </div>
    );
  }

  return (
    <div className="px-4 pt-6 pb-6 space-y-4" data-testid="profile-auth">
      <h1 className="font-display text-[28px] font-bold tracking-tight">
        {t("app.tab_login")}
      </h1>
      <p className="text-[14px] text-white/55 leading-relaxed">
        {t("app.profile_hint")}
      </p>

      <div
        className="bg-[#1c1c1e] rounded-2xl p-4 border border-white/[0.08]"
        data-testid="lang-selector"
      >
        <p className="text-[11px] text-white/55 uppercase font-bold tracking-wider mb-2.5">
          {t("app.lang_label")}
        </p>
        <div className="flex gap-2">
          {languages.map((l) => (
            <button
              key={l.code}
              data-testid={`lang-${l.code}`}
              onClick={() => setLang(l.code)}
              className={`flex-1 flex flex-col items-center gap-1 py-2.5 rounded-2xl border-2 transition-colors ${
                lang === l.code
                  ? "bg-white text-[#0a0a0a] border-white font-bold"
                  : "bg-[#1c1c1e] border-white/[0.08] text-white/80"
              }`}
            >
              <span className="text-lg leading-none">{l.flag}</span>
              <span className="text-[13px] font-semibold">{l.name}</span>
            </button>
          ))}
        </div>
      </div>

      <button
        data-testid="login-google"
        onClick={() => toast("Google sign-in coming soon")}
        className="w-full h-12 bg-[#1c1c1e] border-2 border-white/[0.08] rounded-2xl flex items-center justify-center gap-3 font-bold tracking-wider hover:bg-white/[0.04] transition-colors"
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24">
          <path
            fill="#EA4335"
            d="M5.27 9.76A7.08 7.08 0 0 1 16.46 6.4l3.4-3.4A11.95 11.95 0 0 0 12 0C7.39 0 3.4 2.6 1.46 6.4l3.81 3.36Z"
          />
          <path
            fill="#34A853"
            d="M16.06 18.01a7.27 7.27 0 0 1-3.93 1.08c-3 0-5.55-1.94-6.46-4.65L1.84 17.8A11.99 11.99 0 0 0 12 24a11.4 11.4 0 0 0 7.84-3.07l-3.78-2.92Z"
          />
          <path
            fill="#4A90E2"
            d="M19.85 20.92c2.27-2.16 3.74-5.34 3.74-8.92 0-.71-.07-1.42-.21-2.09H12v4.55h6.46c-.34 1.55-1.22 2.85-2.4 3.74l3.79 2.72Z"
          />
          <path
            fill="#FBBC05"
            d="M5.66 14.45c-.24-.7-.37-1.46-.37-2.45s.14-1.74.37-2.45L1.84 6.16C1.02 8 .55 9.97.55 12s.47 4 1.29 5.84l3.82-3.39Z"
          />
        </svg>
        {t("app.google_signin")}
      </button>

      <div className="flex items-center gap-3 py-1">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-[11px] text-white/55 uppercase font-bold tracking-widest">
          {t("app.or")}
        </span>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          data-testid="tab-login"
          onClick={() => setMode("login")}
          className={`h-12 rounded-2xl text-[13px] font-bold tracking-wider border-2 transition-colors ${
            mode === "login"
              ? "bg-[#007AFF]/10 border-[#007AFF] text-white"
              : "bg-[#1c1c1e] border-white/[0.08] text-white/55"
          }`}
        >
          {t("app.tab_login")}
        </button>
        <button
          data-testid="tab-register"
          onClick={() => setMode("register")}
          className={`h-12 rounded-2xl text-[13px] font-bold tracking-wider border-2 transition-colors ${
            mode === "register"
              ? "bg-[#007AFF]/10 border-[#007AFF] text-white"
              : "bg-[#1c1c1e] border-white/[0.08] text-white/55"
          }`}
        >
          {t("app.tab_register")}
        </button>
      </div>

      <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@example.com"
          data-testid="email-input"
          autoCapitalize="none"
          required
          className="bg-[#1c1c1e] border-white/[0.08] text-white h-12 rounded-2xl px-4 focus:border-[#007AFF] text-[15px]"
        />
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          data-testid="password-input"
          required
          minLength={6}
          className="bg-[#1c1c1e] border-white/[0.08] text-white h-12 rounded-2xl px-4 focus:border-[#007AFF] text-[15px]"
        />
        <Button
          type="submit"
          disabled={loading}
          data-testid="submit-auth"
          className="w-full h-12 bg-[#FF3B30] hover:bg-[#FF4E45] text-white rounded-2xl font-bold tracking-widest text-[14px]"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : mode === "login" ? (
            t("app.submit_login")
          ) : (
            t("app.submit_register")
          )}
        </Button>
      </form>

      <div className="bg-[#1c1c1e] rounded-2xl divide-y divide-white/5 border border-white/[0.08] mt-3">
        <p className="text-[11px] text-white/55 uppercase font-bold tracking-wider px-4 pt-4 pb-2">
          {t("app.legal_label")}
        </p>
        {[
          { icon: ShieldCheck, label: t("app.privacy") },
          { icon: FileText, label: t("app.terms") },
          { icon: HelpCircle, label: t("app.support") },
        ].map((it, i) => (
          <button
            key={i}
            data-testid={`legal-link-${i}`}
            className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-white/[0.02] transition-colors"
          >
            <it.icon className="h-[18px] w-[18px] text-[#06AED5]" />
            <span className="flex-1 text-start text-[15px]">{it.label}</span>
            <ChevronRight className="h-[18px] w-[18px] text-white/30" />
          </button>
        ))}
      </div>
    </div>
  );
}
